import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/pages/Dashboard";
import {
  CheckCircle2, AlertTriangle, AlertOctagon, Info, ArrowRight, ShieldOff,
} from "lucide-react";
import ReadinessDonut from "@/components/ReadinessDonut";

// Compact readiness widget for the Overview dashboard.

const ICONS = {
  ok: { Icon: CheckCircle2, tone: "text-emerald-600" },
  warn: { Icon: AlertTriangle, tone: "text-amber-600" },
  critical: { Icon: AlertOctagon, tone: "text-red-600" },
  info: { Icon: Info, tone: "text-[#2563EB]" },
};

const STATUS_RANK = { critical: 0, warn: 1, info: 2, ok: 3 };

export default function ReadinessWidget() {
  const [data, setData] = useState(null);
  const [conflicts, setConflicts] = useState(null);

  useEffect(() => {
    api.get("/automation/readiness").then((r) => setData(r.data))
      .catch(() => setData(null));
    api.get("/automation/conflicts").then((r) => setConflicts(r.data))
      .catch(() => setConflicts(null));
  }, []);

  if (!data) return null;
  const top = [...data.sections]
    .filter((s) => s.status !== "ok")
    .sort((a, b) =>
      (STATUS_RANK[a.status] ?? 9) - (STATUS_RANK[b.status] ?? 9)
      || b.weight - a.weight)
    .slice(0, 3);

  return (
    <Card title={null}>
      <div data-testid="readiness-widget">
        <div className="flex items-start gap-3">
          <ReadinessDonut value={data.overall_score} size={64}
                          testid="readiness-widget-score" />
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-[#0B1A36] tracking-tight flex items-center gap-1.5">
              Setup health
              <span className="text-[9px] uppercase tracking-widest text-[#1D4ED8] px-1 py-0 rounded bg-[#DBE7FE] border border-[#BFD3FB]"
                data-testid="readiness-widget-advanced">Advanced</span>
            </h3>
            <p className="text-xs text-[#4B5773]"
                data-testid="readiness-widget-band">
              {data.headline}
            </p>
          </div>
        </div>

        {top.length > 0 ? (
          <ul className="mt-3 space-y-1.5">
            {top.map((s) => {
              const { Icon, tone } = ICONS[s.status] || ICONS.info;
              return (
                <li key={s.key}
                    data-testid={`readiness-widget-section-${s.key}`}
                    className="flex items-center gap-2 text-xs">
                  <Icon className={`h-3.5 w-3.5 shrink-0 ${tone}`} />
                  <span className="text-[#0B1A36] truncate">{s.title}</span>
                  <span className="text-[#4B5773] truncate">- {s.headline}</span>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="mt-3 text-xs text-emerald-700 inline-flex items-center gap-1.5">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Everything looks tidy
          </div>
        )}

        <Link to="/app/automation#readiness"
              data-testid="readiness-widget-view-all"
              className="mt-3 inline-flex items-center text-xs text-[#2563EB] hover:underline">
          View the full report
          <ArrowRight className="h-3 w-3 ml-0.5" />
        </Link>

        {conflicts && (
          <Link to="/app/readiness"
                data-testid="readiness-widget-conflicts"
                className="mt-1.5 inline-flex items-center gap-1.5 text-xs hover:underline">
            {conflicts.total === 0 ? (
              <>
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                <span className="text-emerald-700">No setup issues found</span>
              </>
            ) : (
              <>
                {conflicts.summary.automation_blocking > 0 ? (
                  <ShieldOff className="h-3.5 w-3.5 text-purple-700" />
                ) : conflicts.summary.critical > 0 ? (
                  <AlertOctagon className="h-3.5 w-3.5 text-red-600" />
                ) : (
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
                )}
                <span className="text-[#0B1A36]">
                  {conflicts.total} item{conflicts.total !== 1 ? "s" : ""} to review
                  {conflicts.summary.automation_blocking > 0
                    ? ` · ${conflicts.summary.automation_blocking} blocking`
                    : ""}
                </span>
                <ArrowRight className="h-3 w-3 text-[#2563EB]" />
              </>
            )}
          </Link>
        )}
      </div>
    </Card>
  );
}
